import { AiBatchReviewRecordChange } from '@/modules/ai/models/AiBatchReviewRecordChange';
import { Liability } from '@/modules/liability/models/Liability';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { BaseExtractedRecordModel } from '@/modules/ai/models/abstracts/BaseExtractedRecordModel';
import Lang from '@/modules/core/app/services/Lang';

export class ExtractedLiability extends BaseExtractedRecordModel {
  id?: number;
  documentExtractionId?: number;
  liabilityId?: number | null;
  liability?: Liability | null;
  name?: string;
  type?: string;
  currentBalanceAmount?: number;
  estimatedAmount?: number;
  atDeathAmount?: number;
  reimbursementAmount?: number;
  interestRate?: number;
  currencyId?: number;

  constructor(model: Partial<ExtractedLiability> = {}) {
    super(model);

    this.id = model.id;
    this.documentExtractionId = model.documentExtractionId;
    this.liabilityId = model.liabilityId;
    this.name = model.name;
    this.type = model.type;
    this.currentBalanceAmount = model.currentBalanceAmount;
    this.estimatedAmount = model.estimatedAmount;
    this.atDeathAmount = model.atDeathAmount;
    this.reimbursementAmount = model.reimbursementAmount;
    this.interestRate = model.interestRate;
    this.currencyId = model.currencyId;
    this.liability = Liability.mapModelOrNull(model.liability);
    this.rememberMatchOnFile();
  }

  getModelName(): ModelType {
    return ModelType.EXTRACTED_LIABILITY;
  }

  get matchedRecord(): Liability | null {
    return this.liability ?? null;
  }

  protected get matchedRecordKey(): string {
    return 'liability';
  }

  protected get matchedId(): number | null | undefined {
    return this.liabilityId;
  }

  protected set matchedId(id: number | null | undefined) {
    this.liabilityId = id;
  }

  get matchedLabel(): string {
    return this.liability?.name ?? '';
  }

  protected get comparedValues(): (AiBatchReviewRecordChange | null)[] {
    const amount: (value: unknown) => string = (value: unknown): string => this.formatAmount(value);
    const matched: Liability | null = this.matchedRecord;

    return [
      this.changeFor(Lang.t('document.ingestion-review-field-name'), matched?.name, this.name),
      this.changeFor(
        Lang.t('document.ingestion-review-liability-balance'),
        matched?.currentBalanceAmount,
        this.currentBalanceAmount,
        amount
      ),
      this.changeFor(
        Lang.t('liability.estimated-amount'),
        matched?.estimatedAmount,
        this.estimatedAmount,
        amount
      ),
      this.changeFor(
        Lang.t('liability.at-death-amount'),
        matched?.atDeathAmount,
        this.atDeathAmount,
        amount
      ),
      this.changeFor(
        Lang.t('liability.reimbursement-amount'),
        matched?.reimbursementAmount,
        this.reimbursementAmount,
        amount
      ),
      this.changeFor(Lang.t('liability.interest-rate'), matched?.interestRate, this.interestRate)
    ];
  }

  get mainAmount(): number | undefined {
    return this.atDeathAmount ?? this.currentBalanceAmount ?? this.estimatedAmount;
  }

  get formattedAmount(): string {
    return this.formatAmount(this.mainAmount);
  }

  get formattedAmountSubLabel(): string {
    return Lang.t('document.ingestion-review-liability-balance');
  }
}
